import { AvroOptional } from "@indeed/signals-logger";
import { log as logRNHiringEventFound } from "@indeed/signals-schema/com/indeed/signals/schema/hiringevents/RNHiringEventFound";
import { log as logRNHiringEventNotFoundError } from "@indeed/signals-schema/com/indeed/signals/schema/hiringevents/RNHiringEventNotFoundError";
import { log as logRNHiringEventRequestError } from "@indeed/signals-schema/com/indeed/signals/schema/hiringevents/RNHiringEventRequestError";
import { useMemo } from "react";

import {
  type GetHiringEventDataByJobKeyQuery,
  useGetHiringEventDataByJobKeyQuery,
} from "../components/core/HiringEvents/graphql/__generated__/GetHiringEventDataByJobKey.gqlgenerated";

export type HiringEventData = NonNullable<
  GetHiringEventDataByJobKeyQuery["findHiringEvents"]
>["result"][number]["hiringEvent"];

type HiringEventsQueryResult = {
  hiringEvent?: HiringEventData;
  loading: boolean;
  error?: Error;
  called: boolean;
  refetch?: () => void;
};

export const useConditionalHiringEventsData = (
  shouldFetchHiringEventData: boolean,
  jobKey: string | null,
): HiringEventsQueryResult => {
  // Only create the query if it's a hiring event
  const { data, loading, error, called, refetch } = useGetHiringEventDataByJobKeyQuery({
    variables: {
      jobKey: jobKey || "",
    },
    skip: !jobKey || !shouldFetchHiringEventData,
    onCompleted: (data) => {
      const hiringEvent: HiringEventData | undefined =
        data?.findHiringEvents?.result?.[0]?.hiringEvent;
      if (!hiringEvent) {
        logRNHiringEventNotFoundError({
          jobkey: AvroOptional.string(jobKey),
        });
      } else {
        logRNHiringEventFound({
          jobkey: AvroOptional.string(jobKey),
          hiringEventUuid: hiringEvent.id,
        });
      }
    },
    onError: (error) => {
      logRNHiringEventRequestError({
        message: AvroOptional.string(error.message),
        name: AvroOptional.string(error.name),
        stack: AvroOptional.string(error.stack),
      });
    },
  });

  return useMemo(() => {
    if (!shouldFetchHiringEventData) {
      return {
        hiringEvent: undefined,
        loading: false,
        error: undefined,
        called: false,
        refetch: undefined,
      };
    }

    const hiringEvent: HiringEventData | undefined =
      data?.findHiringEvents?.result?.[0]?.hiringEvent;

    return {
      hiringEvent,
      loading,
      error,
      called,
      refetch,
    };
  }, [shouldFetchHiringEventData, data, loading, error, called, refetch]);
};
